import { createFileRoute, Link } from '@tanstack/react-router';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Megaphone, Images, Palette, CalendarDays, Newspaper } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { useMarketingStats, useMarketingRecommendations } from '@/hooks/data/useMarketingInsights';
import { formatNumber } from '@/lib/formatLocale';

export const Route = createFileRoute('/_app/marketing-uebersicht')({
  component: UebersichtPage,
});

function UebersichtPage() {
  const { t } = useTranslation();
  const { data: stats } = useMarketingStats();
  const rec = useMarketingRecommendations();
  const [recs, setRecs] = useState<string[] | null>(null);

  const handleLoadRecs = async () => {
    try {
      const r = await rec.mutateAsync();
      setRecs(r);
    } catch {
      toast.error(t('muebersicht.ai_error', 'Empfehlungen konnten nicht geladen werden.'));
    }
  };

  const kpiTiles: Array<{
    label: string;
    value: number | undefined;
    to?: string;
  }> = [
    {
      label: t('muebersicht.kpi_campaigns', 'Kampagnen'),
      value: stats?.campaigns,
      to: '/marketing',
    },
    {
      label: t('muebersicht.kpi_articles', 'Blog-Artikel'),
      value: stats?.articles,
      to: '/blog',
    },
    {
      label: t('muebersicht.kpi_designs', 'Grafiken'),
      value: stats?.designs,
      to: '/mediathek',
    },
    {
      label: t('muebersicht.kpi_tasks_open', 'Offene Aufgaben'),
      value: stats?.tasks_open,
      to: '/kalender',
    },
    {
      label: t('muebersicht.kpi_tasks_done', 'Erledigt'),
      value: stats?.tasks_done,
    },
    {
      label: t('muebersicht.kpi_last7', 'Letzte 7 Tage'),
      value: stats?.created_last7,
    },
  ];

  const quickLinks = [
    {
      to: '/marketing' as const,
      icon: <Megaphone className="h-5 w-5 text-ink-3" />,
      title: t('muebersicht.q_content', 'Content Studio'),
      desc: t('muebersicht.q_content_d', 'Social-Media-Texte generieren'),
    },
    {
      to: '/mediathek' as const,
      icon: <Images className="h-5 w-5 text-ink-3" />,
      title: t('muebersicht.q_media', 'Mediathek'),
      desc: t('muebersicht.q_media_d', 'Bilder, PDFs, Videos'),
    },
    {
      to: '/design' as const,
      icon: <Palette className="h-5 w-5 text-ink-3" />,
      title: t('muebersicht.q_design', 'Design-Center'),
      desc: t('muebersicht.q_design_d', 'Grafiken & Flyer erstellen'),
    },
    {
      to: '/kalender' as const,
      icon: <CalendarDays className="h-5 w-5 text-ink-3" />,
      title: t('muebersicht.q_calendar', 'Kalender'),
      desc: t('muebersicht.q_calendar_d', 'Aufgaben planen'),
    },
    {
      to: '/blog' as const,
      icon: <Newspaper className="h-5 w-5 text-ink-3" />,
      title: t('muebersicht.q_blog', 'Blog'),
      desc: t('muebersicht.q_blog_d', 'SEO-Artikel schreiben'),
    },
  ];

  return (
    <section className="space-y-6">
      {/* Header */}
      <header>
        <h2 className="font-display text-2xl text-ink">
          {t('muebersicht.title', 'Marketing-Übersicht')}
        </h2>
        <p className="text-sm text-ink-3">
          {t('muebersicht.subtitle', 'Alle Marketing-Aktivitäten auf einen Blick.')}
        </p>
      </header>

      {/* KPI strip */}
      <div className="fm-stagger grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-6">
        {kpiTiles.map((tile) => {
          const body = (
            <>
              <p className="fm-kpi-value">
                {tile.value !== undefined ? formatNumber(tile.value) : '—'}
              </p>
              <p className="fm-kpi-label">{tile.label}</p>
            </>
          );

          if (tile.to) {
            return (
              <Link
                key={tile.label}
                to={tile.to}
                className="fm-kpi block transition hover:border-ink hover:shadow-soft"
              >
                {body}
              </Link>
            );
          }

          return (
            <div key={tile.label} className="fm-kpi">
              {body}
            </div>
          );
        })}
      </div>

      {/* AI-Empfehlungen panel */}
      <div className="rounded-xl border border-[hsl(var(--brand-gold)/0.4)] bg-[hsl(var(--brand-gold)/0.04)] p-5">
        <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
          <h3 className="font-display text-lg text-ink">
            {t('muebersicht.ai_title', 'AI-Empfehlungen')}
          </h3>
          <Button
            size="sm"
            variant="outline"
            onClick={() => void handleLoadRecs()}
            disabled={rec.isPending}
          >
            {rec.isPending
              ? t('muebersicht.loading_ai', 'Zoki denkt nach …')
              : t('muebersicht.ai_load', 'Empfehlungen laden')}
          </Button>
        </div>

        {recs === null ? (
          <p className="text-sm text-ink-3">
            {t(
              'muebersicht.ai_empty',
              'Klicken Sie auf „Empfehlungen laden", um KI-gestützte Marketing-Tipps für Ihr Team zu erhalten.',
            )}
          </p>
        ) : (
          <ol className="list-decimal space-y-2 ps-5 text-sm text-ink">
            {recs.map((r, i) => (
              // Recommendations are AI-generated strings, no stable key available.
              // eslint-disable-next-line react/no-array-index-key
              <li key={i}>{r}</li>
            ))}
          </ol>
        )}
      </div>

      {/* Quick-link cards */}
      <div>
        <h3 className="mb-3 font-display text-lg text-ink">
          {t('muebersicht.quick_title', 'Schnellzugriff')}
        </h3>
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {quickLinks.map((ql) => (
            <Link
              key={ql.to}
              to={ql.to}
              className="rounded-xl border border-line bg-card p-4 transition hover:border-ink/30"
            >
              <div className="mb-2">{ql.icon}</div>
              <p className="text-sm font-semibold text-ink">{ql.title}</p>
              <p className="text-xs text-ink-3">{ql.desc}</p>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}
